import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const CATEGORIAS = ['Writing', 'Listening', 'Reading', 'Speaking'];

const DARK  = [88, 28, 71];
const PINK  = [236, 72, 153];
const WHITE = [255, 255, 255];
const TEXT  = [60, 30, 55];
const MUTED = [180, 130, 160];

function scoreRGB(s) {
  if (s >= 85) return [139, 92, 246];
  if (s >= 70) return [34, 197, 94];
  if (s >= 50) return [245, 158, 11];
  return [239, 68, 68];
}

function scoreLabel(s) {
  if (s === null) return '—';
  if (s >= 85) return 'Excelente';
  if (s >= 70) return 'Bueno';
  if (s >= 50) return 'Aprobado';
  return 'Suspenso';
}

export function generarBoletin(alumno, nivelActivoObj, intentosFiltrados, stats, comentario) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W  = 210;
  const M  = 18;
  const CW = W - M * 2;

  // ── CABECERA ──────────────────────────────────────────────────
  doc.setFillColor(...DARK);
  doc.rect(0, 0, W, 38, 'F');

  // Franja rosa inferior de la cabecera
  doc.setFillColor(...PINK);
  doc.rect(0, 38, W, 2.5, 'F');

  // Franjas decorativas laterales
  doc.setFillColor(116, 50, 100);
  doc.rect(0, 0, 6, 38, 'F');
  doc.setFillColor(...PINK);
  doc.rect(0, 0, 2.5, 38, 'F');

  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('SU ACADEMIA', W / 2, 14, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(252, 207, 232);
  doc.text('BOLETÍN OFICIAL DE NOTAS', W / 2, 22, { align: 'center' });

  const year = new Date().getFullYear();
  doc.setFontSize(8);
  doc.setTextColor(190, 140, 175);
  doc.text(`Año académico ${year} – ${year + 1}`, W / 2, 32, { align: 'center' });

  // ── DATOS ALUMNO ──────────────────────────────────────────────
  let y = 52;

  doc.setTextColor(...DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.text(alumno.nombre, M, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...MUTED);
  doc.text(alumno.email, M, y + 7);

  const tipoLabel = nivelActivoObj.tipo === 'militar' ? 'Militar' : 'Civil';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...PINK);
  doc.text(`${tipoLabel} — Nivel ${nivelActivoObj.nivel}`, W - M, y, { align: 'right' });

  const fechaHoy = new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...MUTED);
  doc.text(`Emitido: ${fechaHoy}`, W - M, y + 7, { align: 'right' });
  doc.text(`${intentosFiltrados.length} evaluación(es) realizadas`, W - M, y + 13, { align: 'right' });

  y += 22;

  doc.setDrawColor(...PINK);
  doc.setLineWidth(0.4);
  doc.line(M, y, W - M, y);

  y += 9;

  // ── RENDIMIENTO POR CATEGORÍA ─────────────────────────────────
  doc.setFillColor(...DARK);
  doc.roundedRect(M, y, CW, 7.5, 1.5, 1.5, 'F');
  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text('RENDIMIENTO POR CATEGORÍA', M + 5, y + 5);

  y += 11;

  const catRows = CATEGORIAS.map(cat => {
    const cs = stats.porCategoria[cat];
    if (cs.esSpeaking) {
      return [
        cat,
        '—',
        cs.notaRaw !== null ? `${cs.notaRaw}/10` : '—',
        cs.avg !== null ? scoreLabel(cs.avg) : '—',
        cs.notaRaw !== null ? `${cs.notaRaw}/10` : '—',
      ];
    }
    return [
      cat,
      String(cs.total),
      cs.avg !== null ? `${cs.avg}%` : '—',
      cs.avg !== null ? scoreLabel(cs.avg) : '—',
      cs.ultimo !== null ? `${cs.ultimo}%` : '—',
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [['Categoría', 'Exámenes', 'Media global', 'Calificación', 'Último exam.']],
    body: catRows,
    theme: 'grid',
    margin: { left: M, right: M },
    headStyles: {
      fillColor: PINK,
      textColor: WHITE,
      fontStyle: 'bold',
      fontSize: 8.5,
      halign: 'center',
      cellPadding: 3,
    },
    bodyStyles: {
      fontSize: 9,
      textColor: TEXT,
      cellPadding: 3,
    },
    columnStyles: {
      0: { halign: 'left',   cellWidth: 38, fontStyle: 'bold' },
      1: { halign: 'center', cellWidth: 28 },
      2: { halign: 'center', cellWidth: 35, fontStyle: 'bold' },
      3: { halign: 'center', cellWidth: 38 },
      4: { halign: 'center', cellWidth: 35 },
    },
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      data.cell.styles.fillColor = data.row.index % 2 === 0 ? [255, 250, 253] : [252, 231, 243];
      if (data.column.index === 2 || data.column.index === 4) {
        const raw = String(data.cell.raw);
        const v = raw.includes('/10') ? parseFloat(raw) * 10 : parseInt(raw);
        if (!isNaN(v)) data.cell.styles.textColor = scoreRGB(v);
      }
      if (data.column.index === 3) {
        const l = data.cell.raw;
        if (l === 'Excelente')      data.cell.styles.textColor = [139, 92, 246];
        else if (l === 'Bueno')     data.cell.styles.textColor = [34, 197, 94];
        else if (l === 'Aprobado')  data.cell.styles.textColor = [245, 158, 11];
        else if (l === 'Suspenso')  data.cell.styles.textColor = [239, 68, 68];
      }
    },
  });

  y = doc.lastAutoTable.finalY + 9;

  // ── HISTORIAL ─────────────────────────────────────────────────
  if (intentosFiltrados.length > 0) {
    if (y > 195) { doc.addPage(); y = 20; }

    doc.setFillColor(...DARK);
    doc.roundedRect(M, y, CW, 7.5, 1.5, 1.5, 'F');
    doc.setTextColor(...WHITE);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('HISTORIAL DE EVALUACIONES', M + 5, y + 5);

    y += 11;

    const histRows = [...intentosFiltrados].reverse().map(i => [
      new Date(i.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }),
      i.evaluacion_titulo,
      i.categoria || '—',
      `${i.puntuacion}%`,
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Fecha', 'Evaluación', 'Categoría', 'Puntuación']],
      body: histRows,
      theme: 'grid',
      margin: { left: M, right: M },
      headStyles: {
        fillColor: PINK,
        textColor: WHITE,
        fontStyle: 'bold',
        fontSize: 8.5,
        halign: 'center',
        cellPadding: 3,
      },
      bodyStyles: {
        fontSize: 8.5,
        textColor: TEXT,
        cellPadding: 2.8,
      },
      columnStyles: {
        0: { cellWidth: 30, halign: 'center' },
        1: { cellWidth: 82 },
        2: { cellWidth: 30, halign: 'center' },
        3: { cellWidth: 32, halign: 'center', fontStyle: 'bold' },
      },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? [255, 250, 253] : [252, 231, 243];
        if (data.column.index === 3) {
          const v = parseInt(data.cell.raw);
          if (!isNaN(v)) data.cell.styles.textColor = scoreRGB(v);
        }
      },
    });

    y = doc.lastAutoTable.finalY + 9;
  }

  // ── OBSERVACIONES ─────────────────────────────────────────────
  if (y > 220) { doc.addPage(); y = 20; }

  doc.setFillColor(...DARK);
  doc.roundedRect(M, y, CW, 7.5, 1.5, 1.5, 'F');
  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text('OBSERVACIONES DEL PROFESOR/A', M + 5, y + 5);

  y += 11;

  const obsH = 34;
  doc.setFillColor(255, 248, 253);
  doc.setDrawColor(...PINK);
  doc.setLineWidth(0.5);
  doc.roundedRect(M, y, CW, obsH, 2, 2, 'FD');

  if (comentario && comentario.trim()) {
    doc.setTextColor(...TEXT);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9.5);
    const lines = doc.splitTextToSize(comentario.trim(), CW - 10);
    doc.text(lines, M + 5, y + 8);
  } else {
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.text('Sin observaciones adicionales.', M + 5, y + 12);
  }

  y += obsH + 12;

  // ── FIRMA Y CERTIFICACIÓN ─────────────────────────────────────
  if (y > 258) { doc.addPage(); y = 20; }

  doc.setDrawColor(...PINK);
  doc.setLineWidth(0.6);
  doc.line(M, y, W - M, y);

  y += 9;

  // Texto certificación izquierda
  doc.setTextColor(...MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text('Este documento certifica oficialmente el rendimiento académico del alumno/a', M, y);
  doc.text(`en el nivel ${nivelActivoObj.nivel}. Emitido y validado por Su Academia.`, M, y + 4.5);

  // Bloque firma derecha
  const sigX = W - M - 58;
  doc.setDrawColor(...DARK);
  doc.setLineWidth(0.3);
  doc.line(sigX, y + 3, sigX + 58, y + 3);
  doc.setTextColor(...DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text('FIRMA Y SELLO OFICIAL', sigX + 29, y + 8, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED);
  doc.setFontSize(7);
  doc.text('Profesor/a Titular — Su Academia', sigX + 29, y + 13, { align: 'center' });

  // Sello circular
  const sX = W - M - 16;
  const sY = y + 7;
  doc.setDrawColor(...PINK);
  doc.setLineWidth(1.2);
  doc.circle(sX, sY, 11, 'S');
  doc.setDrawColor(...DARK);
  doc.setLineWidth(0.5);
  doc.circle(sX, sY, 9, 'S');
  doc.setTextColor(...DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(5);
  doc.text('SU ACADEMIA', sX, sY - 3, { align: 'center' });
  doc.text('• OFICIAL •', sX, sY + 1, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(4.5);
  doc.text(String(year), sX, sY + 5, { align: 'center' });

  y += 22;

  doc.setTextColor(...MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text(`Fecha de emisión: ${fechaHoy}`, M, y);

  // ── PIE EN TODAS LAS PÁGINAS ─────────────────────────────────
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFillColor(...DARK);
    doc.rect(0, 290, W, 7, 'F');
    doc.setFillColor(...PINK);
    doc.rect(0, 290, W, 1.5, 'F');
    doc.setTextColor(190, 140, 175);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text('Su Academia — Documento confidencial', W / 2, 295, { align: 'center' });
  }

  // ── GUARDAR ──────────────────────────────────────────────────
  doc.save(`Boletin_${alumno.nombre.replace(/\s+/g, '_')}_${nivelActivoObj.nivel}_${year}.pdf`);
}
